import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { OrganizationResponse } from '@bm/contracts';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Alert, Button, Card, Field } from '../components/ui';
import { OrgTabs } from '../components/OrgTabs';
import { ModeTabs } from '../components/design/ModeTabs';
import { fileToBase64 } from '../lib/file-upload';
import type { ThemeMode } from '../lib/theme';
import {
  BUILTIN_BOXED_LAYOUT_ENABLED,
  BUILTIN_BOXED_WIDTH,
  BUILTIN_COLOR_DARK,
  BUILTIN_COLOR_LIGHT,
  BUILTIN_GRADIENT_ANGLE,
  BUILTIN_GRADIENT_TO_DARK,
  BUILTIN_GRADIENT_TO_LIGHT,
  BUILTIN_HEADER_BORDER_COLOR_DARK,
  BUILTIN_HEADER_BORDER_COLOR_LIGHT,
  BUILTIN_HEADER_BORDER_ENABLED,
  BUILTIN_HEADER_HEIGHT,
  COLOR_FIELDS,
  TRANSLUCENT_FIELDS,
  type AngleKey,
  type ColorKey,
  type GradientToKey,
  type OpacityKey,
} from '../lib/design-defaults';

interface ColorsFormState {
  colors: Record<ColorKey, string>;
  opacity: Record<OpacityKey, number | undefined>;
  gradientTo: Record<GradientToKey, string>;
  angle: Record<AngleKey, number | undefined>;
  headerHeight: number | undefined;
  headerBorderEnabled: boolean | undefined;
  headerBorderColor: string;
  boxedWidth: number | undefined;
  boxedLayoutEnabled: boolean | undefined;
  pageBackgroundColor: string;
}
const EMPTY_COLORS_STATE: ColorsFormState = {
  colors: {
    primary: '',
    secondary: '',
    headerBackground: '',
    headerText: '',
    sidebarBackground: '',
    contentBackground: '',
  },
  opacity: { sidebarOpacity: undefined, contentOpacity: undefined },
  gradientTo: { sidebarBackgroundTo: '', contentBackgroundTo: '' },
  angle: { sidebarGradientAngle: undefined, contentGradientAngle: undefined },
  headerHeight: undefined,
  headerBorderEnabled: undefined,
  headerBorderColor: '',
  boxedWidth: undefined,
  boxedLayoutEnabled: undefined,
  pageBackgroundColor: '',
};

const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const MAX_BACKGROUND_BYTES = 2 * 1024 * 1024;
const MAX_APP_ICON_BYTES = 2 * 1024 * 1024;

type BoxedBackground = { kind: 'image'; fileId: string } | { kind: 'color'; color: string };

/** The fixed "boxed layout" background is either an uploaded image or a
 * flat color, never both — an image wins if both happen to be set at the
 * same level. The image itself is the same for both modes (no per-theme
 * file uploads, see the Logo/Hintergrundbild cards below); only the
 * flat-color fallback shown when no image is set follows the selected
 * preview mode. Same logic as `resolveBoxedBackground` in AppShell.tsx. */
function resolveBoxedBackground(data: OrganizationResponse | undefined, mode: ThemeMode): BoxedBackground | null {
  if (data?.backgroundImageFileId) return { kind: 'image', fileId: data.backgroundImageFileId };
  const color =
    mode === 'dark'
      ? (data?.colorsDark?.pageBackgroundColor ?? data?.platformDefaultColorsDark?.pageBackgroundColor)
      : data?.colors?.pageBackgroundColor;
  if (color) return { kind: 'color', color };
  if (data?.platformDefaultBackgroundImageFileId) {
    return { kind: 'image', fileId: data.platformDefaultBackgroundImageFileId };
  }
  if (mode === 'light' && data?.platformDefaultColors?.pageBackgroundColor) {
    return { kind: 'color', color: data.platformDefaultColors.pageBackgroundColor };
  }
  return null;
}

export function OrgDesignPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const readOnly = !can('organization.edit');

  const { data } = useQuery({
    queryKey: ['org'],
    queryFn: () => api<OrganizationResponse>('/org', { orgScoped: true }),
  });

  const [mode, setMode] = useState<ThemeMode>('light');
  const builtinColor = mode === 'dark' ? BUILTIN_COLOR_DARK : BUILTIN_COLOR_LIGHT;
  const builtinGradientTo = mode === 'dark' ? BUILTIN_GRADIENT_TO_DARK : BUILTIN_GRADIENT_TO_LIGHT;
  const platformColors = mode === 'dark' ? data?.platformDefaultColorsDark : data?.platformDefaultColors;

  const effectiveLogoFileId = data?.logoFileId ?? data?.platformDefaultLogoFileId ?? null;
  const boxedBackground = resolveBoxedBackground(data, mode);

  const [colorsByMode, setColorsByMode] = useState<Record<ThemeMode, ColorsFormState>>({
    light: EMPTY_COLORS_STATE,
    dark: EMPTY_COLORS_STATE,
  });
  const [saved, setSaved] = useState(false);
  const [backgroundColorSaved, setBackgroundColorSaved] = useState(false);

  const cur = colorsByMode[mode];
  const {
    colors,
    opacity,
    gradientTo,
    angle,
    headerHeight,
    headerBorderEnabled,
    headerBorderColor,
    boxedWidth,
    boxedLayoutEnabled,
    pageBackgroundColor,
  } = cur;

  const patchColorsState = (patch: Partial<ColorsFormState>) =>
    setColorsByMode((s) => ({ ...s, [mode]: { ...s[mode], ...patch } }));
  const setColors = (updater: (c: Record<ColorKey, string>) => Record<ColorKey, string>) =>
    patchColorsState({ colors: updater(cur.colors) });
  const setOpacity = (updater: (o: Record<OpacityKey, number | undefined>) => Record<OpacityKey, number | undefined>) =>
    patchColorsState({ opacity: updater(cur.opacity) });
  const setGradientTo = (updater: (g: Record<GradientToKey, string>) => Record<GradientToKey, string>) =>
    patchColorsState({ gradientTo: updater(cur.gradientTo) });
  const setAngle = (updater: (a: Record<AngleKey, number | undefined>) => Record<AngleKey, number | undefined>) =>
    patchColorsState({ angle: updater(cur.angle) });
  const setHeaderHeight = (v: number | undefined) => patchColorsState({ headerHeight: v });
  const setHeaderBorderEnabled = (v: boolean | undefined) => patchColorsState({ headerBorderEnabled: v });
  const setHeaderBorderColor = (v: string) => patchColorsState({ headerBorderColor: v });
  const setBoxedWidth = (v: number | undefined) => patchColorsState({ boxedWidth: v });
  const setBoxedLayoutEnabled = (v: boolean | undefined) => patchColorsState({ boxedLayoutEnabled: v });
  const setPageBackgroundColor = (v: string) => patchColorsState({ pageBackgroundColor: v });

  useEffect(() => {
    if (!data) return;
    const fromApi = (c: OrganizationResponse['colors']): ColorsFormState => ({
      colors: {
        primary: c?.primary ?? '',
        secondary: c?.secondary ?? '',
        headerBackground: c?.headerBackground ?? '',
        headerText: c?.headerText ?? '',
        sidebarBackground: c?.sidebarBackground ?? '',
        contentBackground: c?.contentBackground ?? '',
      },
      opacity: { sidebarOpacity: c?.sidebarOpacity, contentOpacity: c?.contentOpacity },
      gradientTo: { sidebarBackgroundTo: c?.sidebarBackgroundTo ?? '', contentBackgroundTo: c?.contentBackgroundTo ?? '' },
      angle: { sidebarGradientAngle: c?.sidebarGradientAngle, contentGradientAngle: c?.contentGradientAngle },
      headerHeight: c?.headerHeight,
      headerBorderEnabled: c?.headerBorderEnabled,
      headerBorderColor: c?.headerBorderColor ?? '',
      boxedWidth: c?.boxedWidth,
      boxedLayoutEnabled: c?.boxedLayoutEnabled,
      pageBackgroundColor: c?.pageBackgroundColor ?? '',
    });
    setColorsByMode({ light: fromApi(data.colors), dark: fromApi(data.colorsDark) });
  }, [data]);

  const saveColors = useMutation({
    mutationFn: (body: Record<string, string | number | boolean | undefined>) =>
      api<OrganizationResponse>('/org', {
        method: 'PATCH',
        orgScoped: true,
        body: { [mode === 'dark' ? 'colorsDark' : 'colors']: body },
      }),
    onSuccess: () => {
      setSaved(true);
      void qc.invalidateQueries({ queryKey: ['org'] });
      setTimeout(() => setSaved(false), 2000);
    },
  });

  /** Same body every "Farben" save sends — factored out so the dedicated
   * "Hintergrundfarbe"-Speichern-Button (in the "Hintergrundbild" card) can
   * reuse it too. `PATCH /org` replaces the whole `colors`/`colorsDark`
   * object at once for the active mode (no per-field merge server-side, see
   * mapProfilePatch), so sending only `{ pageBackgroundColor }` from that
   * button would silently wipe out every other already-saved color for this
   * mode — this always sends the complete, current state instead.
   * `override` lets the reset button pass the just-cleared value
   * immediately, without waiting for the async state update. */
  const buildColorsBody = (override?: string): Record<string, string | number | boolean | undefined> => {
    const body: Record<string, string | number | boolean | undefined> = Object.fromEntries(
      Object.entries(colors).map(([k, v]) => [k, v || undefined]),
    );
    body.headerHeight = headerHeight;
    body.headerBorderEnabled = headerBorderEnabled;
    body.headerBorderColor = headerBorderColor || undefined;
    body.boxedWidth = boxedWidth;
    body.boxedLayoutEnabled = boxedLayoutEnabled;
    for (const [, opacityKey, gradientToKey, angleKey] of TRANSLUCENT_FIELDS) {
      body[opacityKey] = opacity[opacityKey];
      body[gradientToKey] = gradientTo[gradientToKey] || undefined;
      body[angleKey] = angle[angleKey];
    }
    body.pageBackgroundColor = (override ?? pageBackgroundColor) || undefined;
    return body;
  };

  const uploadLogo = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > MAX_LOGO_BYTES) throw new Error('Logo darf höchstens 2 MB groß sein.');
      const contentBase64 = await fileToBase64(file);
      return api('/org/logo', {
        method: 'PUT',
        orgScoped: true,
        body: { filename: file.name, mimeType: file.type, contentBase64 },
      });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['org'] }),
  });

  const uploadBackground = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > MAX_BACKGROUND_BYTES) throw new Error('Hintergrundbild darf höchstens 2 MB groß sein.');
      const contentBase64 = await fileToBase64(file);
      return api('/org/background', {
        method: 'PUT',
        orgScoped: true,
        body: { filename: file.name, mimeType: file.type, contentBase64 },
      });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['org'] }),
  });

  const resetLogo = useMutation({
    mutationFn: () => api('/org/logo', { method: 'DELETE', orgScoped: true }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['org'] }),
  });

  const uploadAppIcon = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > MAX_APP_ICON_BYTES) throw new Error('Symbol darf höchstens 2 MB groß sein.');
      const contentBase64 = await fileToBase64(file);
      return api('/org/app-icon', {
        method: 'PUT',
        orgScoped: true,
        body: { filename: file.name, mimeType: file.type, contentBase64 },
      });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['org'] }),
  });

  const resetAppIcon = useMutation({
    mutationFn: () => api('/org/app-icon', { method: 'DELETE', orgScoped: true }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['org'] }),
  });

  const resetBackground = useMutation({
    mutationFn: () => api('/org/background', { method: 'DELETE', orgScoped: true }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['org'] }),
  });

  return (
    <div className="space-y-4">
      <h1 className="hidden text-xl font-semibold md:block">Verein</h1>
      <OrgTabs />

      <Card title="Logo">
        <div className="flex items-center gap-4">
          {effectiveLogoFileId ? (
            <img
              src={`/api/org/files/${effectiveLogoFileId}`}
              alt="Vereinslogo"
              className="h-16 w-16 rounded border border-slate-200 object-contain"
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded border border-dashed border-slate-300 text-xs text-slate-400">
              kein Logo
            </div>
          )}
          {!readOnly && (
            <div className="min-w-0 flex-1">
              <input
                type="file"
                className="block w-full min-w-0"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                disabled={uploadLogo.isPending}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) uploadLogo.mutate(file);
                }}
              />
              <p className="mt-1 text-xs text-slate-400">
                PNG, JPEG, WebP oder SVG · max. 2 MB. Erscheint in Kopf- und Seitenleiste, gleich in
                Light und Dark Mode.{' '}
                {data?.logoFileId
                  ? 'Eigenes Logo hochgeladen.'
                  : data?.platformDefaultLogoFileId
                    ? 'Aktuell: Standardlogo der Plattform.'
                    : ''}
              </p>
              {data?.logoFileId && (
                <button
                  type="button"
                  className="mt-1 text-xs text-slate-400 hover:text-slate-600"
                  disabled={resetLogo.isPending}
                  onClick={() => resetLogo.mutate()}
                >
                  auf Standard zurücksetzen
                </button>
              )}
            </div>
          )}
        </div>
        {uploadLogo.isError && (
          <Alert>{uploadLogo.error instanceof Error ? uploadLogo.error.message : 'Upload fehlgeschlagen.'}</Alert>
        )}
      </Card>

      <Card title="App-Icon (Homescreen-Symbol)">
        <p className="mb-3 text-xs text-slate-400">
          Wird angezeigt, wenn die App über die eigene Domain (Verwaltung › Verein › Eigene
          Domain) zum Homescreen hinzugefügt oder als App installiert wird. Ohne eigene Domain
          oder ohne eigenes Symbol zeigt das Gerät das allgemeine Plattform-Symbol.
        </p>
        <div className="flex items-center gap-4">
          {data?.appIconFileId ? (
            <img
              src={`/api/org/files/${data.appIconFileId}`}
              alt="App-Icon"
              className="h-16 w-16 rounded border border-slate-200 object-contain"
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded border border-dashed border-slate-300 text-xs text-slate-400">
              kein Symbol
            </div>
          )}
          {!readOnly && (
            <div className="min-w-0 flex-1">
              <input
                type="file"
                className="block w-full min-w-0"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                disabled={uploadAppIcon.isPending}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) uploadAppIcon.mutate(file);
                }}
              />
              <p className="mt-1 text-xs text-slate-400">
                PNG, JPEG, WebP oder SVG · max. 2 MB. Ein weißer/transparenter Rand um das Motiv
                wird automatisch entfernt und das Motiv auf die volle Kachel skaliert.
              </p>
              {data?.appIconFileId && (
                <button
                  type="button"
                  className="mt-1 text-xs text-slate-400 hover:text-slate-600"
                  disabled={resetAppIcon.isPending}
                  onClick={() => resetAppIcon.mutate()}
                >
                  entfernen
                </button>
              )}
            </div>
          )}
        </div>
        {uploadAppIcon.isError && (
          <Alert>{uploadAppIcon.error instanceof Error ? uploadAppIcon.error.message : 'Upload fehlgeschlagen.'}</Alert>
        )}
      </Card>

      <Card title="Hintergrundbild">
        <p className="mb-3 text-xs text-slate-400">
          Läuft hinter der zentrierten Boxed-Layout-Fläche, bleibt beim Scrollen fest stehen —
          entweder ein Bild oder eine Farbe, nie beides: ein hochgeladenes Bild hat Vorrang vor
          einer gesetzten Farbe (auf jeder Ebene, eigener Wert und Plattform-Standard für sich).
          Das Bild selbst gilt für Light und Dark Mode gleichermaßen; die Ersatzfarbe unten kann
          pro Modus unterschiedlich sein (siehe Tabs weiter unten).
        </p>
        <div className="flex items-center gap-4">
          {boxedBackground?.kind === 'image' ? (
            <img
              src={`/api/org/files/${boxedBackground.fileId}`}
              alt="Hintergrundbild"
              className="h-16 w-24 rounded border border-slate-200 object-cover"
            />
          ) : boxedBackground?.kind === 'color' ? (
            <div
              className="h-16 w-24 rounded border border-slate-200"
              style={{ backgroundColor: boxedBackground.color }}
            />
          ) : (
            <div className="flex h-16 w-24 items-center justify-center rounded border border-dashed border-slate-300 text-xs text-slate-400">
              kein Bild
            </div>
          )}
          {!readOnly && (
            <div className="min-w-0 flex-1">
              <input
                type="file"
                className="block w-full min-w-0"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                disabled={uploadBackground.isPending}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) uploadBackground.mutate(file);
                }}
              />
              <p className="mt-1 text-xs text-slate-400">
                PNG, JPEG, WebP oder SVG · max. 2 MB.{' '}
                {data?.backgroundImageFileId
                  ? 'Eigenes Bild hochgeladen.'
                  : data?.platformDefaultBackgroundImageFileId
                    ? 'Aktuell: Standardbild der Plattform.'
                    : ''}
              </p>
              {data?.backgroundImageFileId && (
                <button
                  type="button"
                  className="mt-1 text-xs text-slate-400 hover:text-slate-600"
                  disabled={resetBackground.isPending}
                  onClick={() => resetBackground.mutate()}
                >
                  auf Standard zurücksetzen
                </button>
              )}
            </div>
          )}
        </div>

        <div className="mt-4 border-t border-slate-100 pt-4">
          {backgroundColorSaved && <Alert kind="success">Gespeichert.</Alert>}
          <Field label={`Hintergrundfarbe (statt Bild) — ${mode === 'dark' ? 'Dark' : 'Light'} Mode`}>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="color"
                value={pageBackgroundColor || platformColors?.pageBackgroundColor || (mode === 'dark' ? '#0f172a' : '#f8fafc')}
                disabled={readOnly}
                onChange={(e) => setPageBackgroundColor(e.target.value)}
                className="h-9 w-12 rounded border border-slate-300"
              />
              {!readOnly && (
                <Button
                  type="button"
                  disabled={saveColors.isPending}
                  onClick={() =>
                    saveColors.mutate(buildColorsBody(), {
                      onSuccess: () => {
                        setBackgroundColorSaved(true);
                        setTimeout(() => setBackgroundColorSaved(false), 2000);
                      },
                    })
                  }
                >
                  Speichern
                </Button>
              )}
              {pageBackgroundColor && !readOnly && (
                <button
                  type="button"
                  className="text-xs text-slate-400 hover:text-slate-600"
                  disabled={saveColors.isPending}
                  onClick={() => {
                    setPageBackgroundColor('');
                    saveColors.mutate(buildColorsBody(''), {
                      onSuccess: () => {
                        setBackgroundColorSaved(true);
                        setTimeout(() => setBackgroundColorSaved(false), 2000);
                      },
                    });
                  }}
                >
                  auf Standard zurücksetzen
                </button>
              )}
            </div>
          </Field>
        </div>
      </Card>

      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-slate-500">Farben</h2>
        <ModeTabs mode={mode} onChange={setMode} />
      </div>

      <Card title="Farben">
        {saved && <Alert kind="success">Gespeichert.</Alert>}
        <p className="mb-3 text-xs text-slate-400">
          Gilt nur für den {mode === 'dark' ? 'Dark' : 'Light'} Mode — der jeweils andere Modus hat
          seine eigene Einstellung (Tabs oben). Ohne eigene Auswahl gilt der von der Plattform
          vorgegebene Standard für diesen Modus (oder, falls auch der nicht gesetzt ist, der
          eingebaute App-Standard). „Auf Standard zurücksetzen" macht eine eigene Auswahl
          rückgängig, statt sie auf einen festen Wert zu setzen.
        </p>
        <form
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            saveColors.mutate(buildColorsBody());
          }}
        >
          {COLOR_FIELDS.map(([key, label]) => {
            const fallback = platformColors?.[key] ?? builtinColor[key];
            return (
              <Field key={key} label={label}>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="color"
                    value={colors[key] || fallback}
                    disabled={readOnly}
                    onChange={(e) => setColors((c) => ({ ...c, [key]: e.target.value }))}
                    className="h-9 w-12 rounded border border-slate-300"
                  />
                  {colors[key] && !readOnly && (
                    <button
                      type="button"
                      className="text-xs text-slate-400 hover:text-slate-600"
                      onClick={() => setColors((c) => ({ ...c, [key]: '' }))}
                    >
                      auf Standard zurücksetzen
                    </button>
                  )}
                </div>
              </Field>
            );
          })}
          <Field label="Kopfleiste — Höhe (px)">
            {(() => {
              const fallback = platformColors?.headerHeight ?? BUILTIN_HEADER_HEIGHT;
              return (
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="range"
                    min={40}
                    max={160}
                    step={4}
                    value={headerHeight ?? fallback}
                    disabled={readOnly}
                    onChange={(e) => setHeaderHeight(Number(e.target.value))}
                    className="w-32"
                  />
                  <span className="w-12 text-xs text-slate-500">{headerHeight ?? fallback}px</span>
                  {headerHeight !== undefined && !readOnly && (
                    <button
                      type="button"
                      className="text-xs text-slate-400 hover:text-slate-600"
                      onClick={() => setHeaderHeight(undefined)}
                    >
                      auf Standard zurücksetzen
                    </button>
                  )}
                </div>
              );
            })()}
          </Field>
          <Field label="Rahmen Header">
            {(() => {
              const fallback = platformColors?.headerBorderEnabled ?? BUILTIN_HEADER_BORDER_ENABLED;
              return (
                <select
                  className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent disabled:bg-slate-50"
                  disabled={readOnly}
                  value={headerBorderEnabled === undefined ? '' : String(headerBorderEnabled)}
                  onChange={(e) =>
                    setHeaderBorderEnabled(e.target.value === '' ? undefined : e.target.value === 'true')
                  }
                >
                  <option value="">Vom Plattform-Standard übernehmen ({fallback ? 'an' : 'aus'})</option>
                  <option value="true">Aktiviert</option>
                  <option value="false">Deaktiviert</option>
                </select>
              );
            })()}
          </Field>
          <Field label="Rahmen Header — Farbe">
            {(() => {
              const fallback =
                platformColors?.headerBorderColor ??
                (mode === 'dark' ? BUILTIN_HEADER_BORDER_COLOR_DARK : BUILTIN_HEADER_BORDER_COLOR_LIGHT);
              return (
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="color"
                    value={headerBorderColor || fallback}
                    disabled={readOnly}
                    onChange={(e) => setHeaderBorderColor(e.target.value)}
                    className="h-9 w-12 rounded border border-slate-300"
                  />
                  {headerBorderColor && !readOnly && (
                    <button
                      type="button"
                      className="text-xs text-slate-400 hover:text-slate-600"
                      onClick={() => setHeaderBorderColor('')}
                    >
                      auf Standard zurücksetzen
                    </button>
                  )}
                </div>
              );
            })()}
          </Field>
          <Field label="Boxed Layout">
            {(() => {
              const fallback = platformColors?.boxedLayoutEnabled ?? BUILTIN_BOXED_LAYOUT_ENABLED;
              return (
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent disabled:bg-slate-50"
                    disabled={readOnly}
                    value={boxedLayoutEnabled === undefined ? '' : String(boxedLayoutEnabled)}
                    onChange={(e) => setBoxedLayoutEnabled(e.target.value === '' ? undefined : e.target.value === 'true')}
                  >
                    <option value="">Vom Plattform-Standard übernehmen ({fallback ? 'an' : 'aus'})</option>
                    <option value="true">Aktiviert</option>
                    <option value="false">Deaktiviert (volle Breite)</option>
                  </select>
                </div>
              );
            })()}
          </Field>
          <Field label="Boxed Layout — Breite (px)">
            {(() => {
              const fallback = platformColors?.boxedWidth ?? BUILTIN_BOXED_WIDTH;
              return (
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="range"
                    min={600}
                    max={2400}
                    step={8}
                    value={boxedWidth ?? fallback}
                    disabled={readOnly}
                    onChange={(e) => setBoxedWidth(Number(e.target.value))}
                    className="w-32"
                  />
                  <span className="w-14 text-xs text-slate-500">{boxedWidth ?? fallback}px</span>
                  {boxedWidth !== undefined && !readOnly && (
                    <button
                      type="button"
                      className="text-xs text-slate-400 hover:text-slate-600"
                      onClick={() => setBoxedWidth(undefined)}
                    >
                      auf Standard zurücksetzen
                    </button>
                  )}
                </div>
              );
            })()}
          </Field>
          {TRANSLUCENT_FIELDS.map(([colorKey, opacityKey, gradientToKey, angleKey, label]) => {
            const fallback = platformColors?.[colorKey] ?? builtinColor[colorKey];
            const fallbackTo = platformColors?.[gradientToKey] ?? builtinGradientTo[gradientToKey];
            const fallbackOpacity = platformColors?.[opacityKey] ?? 1;
            const fallbackAngle = platformColors?.[angleKey] ?? BUILTIN_GRADIENT_ANGLE;
            const effectiveOpacity = opacity[opacityKey] ?? fallbackOpacity;
            const effectiveAngle = angle[angleKey] ?? fallbackAngle;
            const gradientOn = !!gradientTo[gradientToKey];
            return (
              <Field key={colorKey} label={label}>
                <div className="space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="color"
                      value={colors[colorKey] || fallback}
                      disabled={readOnly}
                      onChange={(e) => setColors((c) => ({ ...c, [colorKey]: e.target.value }))}
                      className="h-9 w-12 rounded border border-slate-300"
                    />
                    {gradientOn && (
                      <>
                        <span className="text-xs text-slate-400">→</span>
                        <input
                          type="color"
                          value={gradientTo[gradientToKey] || fallbackTo}
                          disabled={readOnly}
                          onChange={(e) =>
                            setGradientTo((g) => ({ ...g, [gradientToKey]: e.target.value }))
                          }
                          className="h-9 w-12 rounded border border-slate-300"
                        />
                      </>
                    )}
                    {colors[colorKey] && !readOnly && (
                      <button
                        type="button"
                        className="text-xs text-slate-400 hover:text-slate-600"
                        onClick={() => {
                          setColors((c) => ({ ...c, [colorKey]: '' }));
                          setOpacity((o) => ({ ...o, [opacityKey]: undefined }));
                          setGradientTo((g) => ({ ...g, [gradientToKey]: '' }));
                          setAngle((a) => ({ ...a, [angleKey]: undefined }));
                        }}
                      >
                        auf Standard zurücksetzen
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={5}
                      // stored as opacity (1 = opaque) but shown/edited as
                      // transparency (0% = opaque, matching the pre-existing
                      // fully-covering default) — the more intuitive framing
                      value={Math.round((1 - effectiveOpacity) * 100)}
                      disabled={readOnly}
                      onChange={(e) => {
                        const transparencyPct = Number(e.target.value);
                        setOpacity((o) => ({ ...o, [opacityKey]: 1 - transparencyPct / 100 }));
                        // touching transparency without an explicit color yet —
                        // anchor it to the fallback shown above so it persists
                        setColors((c) => ({ ...c, [colorKey]: c[colorKey] || fallback }));
                      }}
                      className="w-32"
                    />
                    <span className="w-10 text-xs text-slate-500">
                      {Math.round((1 - effectiveOpacity) * 100)}%
                    </span>
                    <span className="text-xs text-slate-400">Transparenz</span>
                  </div>
                  <label className="flex items-center gap-1.5 text-xs text-slate-500">
                    <input
                      type="checkbox"
                      checked={gradientOn}
                      disabled={readOnly}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setColors((c) => ({ ...c, [colorKey]: c[colorKey] || fallback }));
                          setGradientTo((g) => ({ ...g, [gradientToKey]: fallbackTo }));
                        } else {
                          setGradientTo((g) => ({ ...g, [gradientToKey]: '' }));
                        }
                      }}
                    />
                    Verlauf
                  </label>
                  {gradientOn && (
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        type="range"
                        min={0}
                        max={360}
                        step={15}
                        value={effectiveAngle}
                        disabled={readOnly}
                        onChange={(e) =>
                          setAngle((a) => ({ ...a, [angleKey]: Number(e.target.value) }))
                        }
                        className="w-32"
                      />
                      <span className="w-10 text-xs text-slate-500">{effectiveAngle}°</span>
                      <span className="text-xs text-slate-400">Winkel</span>
                    </div>
                  )}
                </div>
              </Field>
            );
          })}
          {!readOnly && (
            <div className="sm:col-span-2">
              <Button type="submit" disabled={saveColors.isPending}>
                Speichern
              </Button>
            </div>
          )}
        </form>
      </Card>
    </div>
  );
}
